import { useContext } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { getItemsByIds, getItemTypes } from '../../services/items';
import { GlobalContext } from '../../context/GlobalState';
import { addActionItem } from '../../services/actionItems';
import { errorNoticeDefault } from '../../utils/notice';
import { ActionItem, Card, Item, ItemTypes } from '../../@types/domain';

export function useGetItemTypes(): UseQueryResult<ItemTypes[], Error> {
  const { retrospective } = useContext(GlobalContext);

  return useQuery<ItemTypes[], Error, ItemTypes[]>(
    ['getItemTypes', retrospective?.boardId],
    () =>
      retrospective?.boardId
        ? getItemTypes(retrospective?.boardId)
        : Promise.resolve([]),
    {
      enabled: !!retrospective?.boardId,
    }
  );
}

export function useGetPlatformActionItem(
  options: UseQueryOptions<Item[], Error, Item[]>
): UseQueryResult<Item[], Error> {
  const { cards, currentRetrospectiveId } = useContext(GlobalContext);

  const actionItems = cards.reduce((accumulator, card) => {
    return [...accumulator, ...card.actionItems];
  }, [] as ActionItem[]);

  const platformIds = actionItems
    .map<number | undefined>((actionItem) => actionItem.platformActionItemId)
    .filter(Boolean) as number[];

  const plainTextItemNames = actionItems
    .map<string | undefined>((actionItem) => actionItem.text)
    .filter(Boolean) as string[];

  return useQuery<Item[], Error, Item[]>(
    [
      'getPlatformItemsByIds',
      currentRetrospectiveId,
      ...platformIds,
      actionItems.length,
      plainTextItemNames,
    ],
    () => {
      return getItemsByIds(platformIds);
    },
    options
  );
}

type NewActionItem = Pick<ActionItem, 'text' | 'platformActionItemId'> & {
  type: ActionItem['type'];
};

export function useAddActionItemMutation(cardId: Card['id']) {
  const { retrospective } = useContext(GlobalContext);
  const queryClient = useQueryClient();
  return useMutation<void, Error, NewActionItem>(
    ['actionItems', 'addActionItem', cardId],
    (actionItem) =>
      retrospective
        ? addActionItem(retrospective?.id, cardId, actionItem)
        : Promise.reject('Retrospective not found'),
    {
      onError(error) {
        errorNoticeDefault(error);
      },
      onSuccess() {
        return queryClient.invalidateQueries(['getPlatformItemsByIds']);
      },
    }
  );
}
